(() => {
  'use strict';

  /**
   * CarPoster Analytics Bridge v1
   *
   * Purpose: provide the checkout Worker with the existing GA4 browser/session
   * identifiers so a server-side `purchase` can join the same GA4 session.
   *
   * Privacy:
   * - Shopify Customer Privacy is the source of truth.
   * - Nothing is persisted by this helper.
   * - GA cookies are read only when analytics processing is explicitly allowed.
   * - Marketing consent is carried separately so the backend can set Google ad
   *   consent without conflating analytics and marketing permission.
   */

  const VERSION = 1;
  const MAX_COOKIES = 8;
  const MAX_VALUE = 512;

  function clamp(value, max = MAX_VALUE) {
    const text = String(value ?? '').trim();
    return text ? text.slice(0, max) : null;
  }

  function nowIso() {
    return new Date().toISOString();
  }

  function cookieMap() {
    const out = {};
    try {
      String(document.cookie || '').split(';').forEach((part) => {
        const idx = part.indexOf('=');
        if (idx < 0) return;
        const rawName = part.slice(0, idx).trim();
        const rawValue = part.slice(idx + 1).trim();
        if (!rawName) return;
        let name = rawName;
        let value = rawValue;
        try { name = decodeURIComponent(rawName); } catch (_) {}
        try { value = decodeURIComponent(rawValue); } catch (_) {}
        out[name] = String(value).slice(0, MAX_VALUE);
      });
    } catch (_) {}
    return out;
  }

  function privacyState() {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy) {
      return {
        apiAvailable: false,
        analytics: null,
        marketing: null,
        saleOfData: null,
        canMeasure: false,
      };
    }

    let analytics = null;
    let marketing = null;
    let saleOfData = null;
    try {
      if (typeof privacy.analyticsProcessingAllowed === 'function') {
        analytics = Boolean(privacy.analyticsProcessingAllowed());
      }
    } catch (_) {}
    try {
      if (typeof privacy.marketingAllowed === 'function') {
        marketing = Boolean(privacy.marketingAllowed());
      }
    } catch (_) {}
    try {
      if (typeof privacy.saleOfDataAllowed === 'function') {
        saleOfData = Boolean(privacy.saleOfDataAllowed());
      }
    } catch (_) {}

    return {
      apiAvailable: true,
      analytics,
      marketing,
      saleOfData,
      canMeasure: analytics === true,
    };
  }

  function get() {
    const privacy = privacyState();
    if (!privacy.apiAvailable || !privacy.canMeasure) return null;

    const cookies = cookieMap();
    const clientIdCookie = clamp(cookies._ga);
    const sessionCookies = {};
    Object.keys(cookies)
      .filter((name) => /^_ga_[A-Z0-9_\-]{4,80}$/i.test(name))
      .slice(0, MAX_COOKIES)
      .forEach((name) => {
        const value = clamp(cookies[name]);
        if (value) sessionCookies[name] = value;
      });

    return {
      version: VERSION,
      consent: {
        source: 'SHOPIFY_CUSTOMER_PRIVACY',
        analyticsAllowed: privacy.analytics === true,
        marketingAllowed: privacy.marketing === true,
        saleOfDataAllowed: typeof privacy.saleOfData === 'boolean' ? privacy.saleOfData : null,
        checkedAt: nowIso(),
      },
      ga4: {
        clientIdCookie,
        sessionCookies,
        capturedAt: nowIso(),
        pageLocation: clamp(window.location.href, 2048),
        pageReferrer: clamp(document.referrer || '', 2048),
      },
    };
  }

  function loadCustomerPrivacyApi() {
    if (window.Shopify?.customerPrivacy) return;
    const loader = window.Shopify?.loadFeatures;
    if (typeof loader !== 'function') return;
    try {
      loader([{ name: 'consent-tracking-api', version: '0.1' }], () => {});
    } catch (_) {}
  }

  window.CarPosterAnalytics = Object.freeze({
    version: VERSION,
    consentAware: true,
    get,
    privacy: privacyState,
  });

  loadCustomerPrivacyApi();
  document.addEventListener('DOMContentLoaded', loadCustomerPrivacyApi, { once: true });
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', loadCustomerPrivacyApi);
  document.addEventListener('visitorConsentCollected', loadCustomerPrivacyApi);
})();
